// src/frontend_react/src/components/ui/Modal/Modal.js
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './Modal.module.css';
import Button from '../Button/Button';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  className = '',
  backdropClassName = '',
  headerClassName = '',
  titleClassName = '',
  bodyClassName = '',
  footerClassName = '',
  closeOnBackdropClick = true,
  showCloseButton = true,
  ariaLabelledBy,
  ariaDescribedBy,
  style,
  alignSelf,
  ...rest 
}) => {
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      const modalElement = document.getElementById('cipher-modal-content');
      if (modalElement) {
        const focusableElements = modalElement.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContentId = ariaDescribedBy || (children ? 'cipher-modal-body-content' : undefined);
  const modalTitleId = ariaLabelledBy || (title ? 'cipher-modal-title' : undefined);

  const dialogStyle = {};
  if (alignSelf) {
    dialogStyle.alignSelf = alignSelf;
  }

  const modalJsx = (
    <div
      className={`${styles.modalBackdrop} ${backdropClassName}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
      aria-describedby={modalContentId}
      data-testid={rest['data-testid']}
    >
      <div
        id="cipher-modal-content"
        className={`${styles.modalDialog} ${styles[size] || ''} ${className}`}
        style={{ ...dialogStyle, ...(rest.style || {}), ...(!styles[size] && typeof size === 'string' ? { maxWidth: size } : {}) }}
      >
        {(title || (showCloseButton && onClose)) && (
          <div className={`${styles.modalHeader} ${headerClassName}`}>
            {title && (
              <h3 id={modalTitleId} className={`${styles.modalTitle} ${titleClassName}`}>
                {title}
              </h3>
            )}
            {showCloseButton && onClose && (
              <Button
                variant="ghost"
                onClick={onClose}
                className={styles.closeButton}
                aria-label="Close modal"
              >
                ×
              </Button>
            )}
          </div>
        )}

        <div id={modalContentId} className={`${styles.modalBody} ${bodyClassName}`}>
          {children}
        </div>

        {footer && (
          <div className={`${styles.modalFooter} ${footerClassName}`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalJsx, document.body);
};

export default Modal;